import { AlertTriangle, ChevronDown } from "lucide-react";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import type { MockValidationIssue } from "@/lib/school-mock-schema";

type Props = {
  unitName?: string | null;
  stage: "input" | "output";
  issues: MockValidationIssue[];
  thrown?: string;
};

/**
 * Tydlig felyta när skolans mockdata är ofullständig eller trasig.
 * Visas istället för profilinnehållet så användaren förstår vad som hänt.
 */
export function MockDataError({ unitName, stage, issues, thrown }: Props) {
  const [open, setOpen] = useState(false);
  const stageLabel =
    stage === "input"
      ? "Skolobjektet saknar fält vi behöver för att bygga profilen."
      : "Den genererade profilen klarade inte valideringen.";

  return (
    <section
      role="alert"
      aria-live="polite"
      className="mx-auto my-10 max-w-3xl rounded-card border border-warning/40 bg-warning/10 p-card shadow-1 p-card"
    >
      <div className="flex items-start gap-4">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-card bg-warning/15 text-warning-ink">
          <AlertTriangle className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="eyebrow text-warning-ink">Profilen kunde inte visas</p>
          <h1 className="mt-1 font-display text-h2 leading-tight text-ink">
            {unitName ? `${unitName} — ofullständiga uppgifter` : "Ofullständiga uppgifter"}
          </h1>
          <p className="mt-2 text-small text-ink-muted">{stageLabel}</p>

          <ul className="mt-4 space-y-1.5 text-small text-ink">
            {issues.slice(0, 6).map((i, idx) => (
              <li key={`${i.path}-${idx}`} className="flex gap-2">
                <span aria-hidden className="mt-1 h-1.5 w-1.5 shrink-0 rounded-pill bg-warning" />
                <span>
                  <span className="mono text-small text-ink-muted">{i.path}</span>
                  <span className="mx-2 text-ink-muted">·</span>
                  <span>{i.message}</span>
                </span>
              </li>
            ))}
            {issues.length > 6 && (
              <li className="text-small text-ink-muted">…och {issues.length - 6} till.</li>
            )}
          </ul>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link
              to="/skolor"
              className="inline-flex items-center rounded-card bg-primary px-4 py-2 text-small font-semibold text-primary-foreground transition hover:bg-primary-hover"
            >
              Tillbaka till söket
            </Link>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-card border border-border bg-background px-3 py-2 text-small font-medium text-ink-muted transition hover:border-primary hover:text-primary"
              aria-expanded={open}
            >
              Teknisk information
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
            </button>
          </div>

          {open && (
            <pre className="mono mt-4 max-h-64 overflow-auto rounded-card border border-border bg-surface p-card text-small leading-relaxed text-ink-muted">
{JSON.stringify({ stage, issues, thrown }, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </section>
  );
}
